import { useCallback, useMemo, useState } from 'react';
import { SerializedSmartLinksListResponse } from 'fym-common/src/db/serializersTypes';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { useGetSmartLinksQuery } from '../../../redux/api/fymAPI';
import { SmartLinkFilter } from '../smlFilterTabs/types';
import { SMARTLINKS_PAGE_SIZE, smartLinksStatusOf } from './smartLinksOverviewQuery';

export interface SmartLinksGrid {
  /** The first request of the tab. Its cards come first; its count is the tab count. */
  firstPage?: SerializedSmartLinksListResponse;
  firstPageLoading: boolean;
  firstPageError: ErrorAPIResult['error'] | undefined;
  retryFirstPage: () => void;
  /** The requests that the grid shows now (1 = only the first one). */
  pageCount: number;
  /** The pages of the account with the same search, every status: the "of M" of the count. */
  totalOfAllTabs?: number;
  /** True while there are more cards than the grid shows. */
  hasMore: boolean;
  /** True while the newest "Show more" request loads. */
  loadingMore: boolean;
  moreError: ErrorAPIResult['error'] | undefined;
  retryMore: () => void;
  showMore: () => void;
}

/**
 * Every SmartLink of the tab, loaded in requests of `SMARTLINKS_PAGE_SIZE` cards. The grid
 * shows the first request, and each "Show more" adds the next one (`SmartLinksGridPage` reads
 * it). The caller keys the grid by tab and search, so a change starts from one request again.
 */
export function useSmartLinksGrid(accountId: number, tab: SmartLinkFilter, search: string): SmartLinksGrid {
  const [pageCount, setPageCount] = useState(1);
  const status = smartLinksStatusOf(tab);
  const trimmedSearch = search.trim();
  const searchArg = trimmedSearch === '' ? undefined : trimmedSearch;

  const first = useGetSmartLinksQuery({
    accountId,
    page: 0,
    limit: SMARTLINKS_PAGE_SIZE,
    search: searchArg,
    status,
  });
  // On the All tab this is the same request as `first`, so RTK Query sends it once.
  const everyStatus = useGetSmartLinksQuery({
    accountId,
    page: 0,
    limit: SMARTLINKS_PAGE_SIZE,
    search: searchArg,
  });
  const last = useGetSmartLinksQuery(
    { accountId, page: pageCount - 1, limit: SMARTLINKS_PAGE_SIZE, search: searchArg, status },
    { skip: pageCount <= 1 }
  );

  const { refetch: refetchFirst } = first;
  const { refetch: refetchLast } = last;
  const retryFirstPage = useCallback(() => {
    refetchFirst().catch(console.error);
  }, [refetchFirst]);
  const retryMore = useCallback(() => {
    refetchLast().catch(console.error);
  }, [refetchLast]);

  const totalPages = first.data?.totalPages ?? 0;
  const loadingMore = pageCount > 1 && last.isFetching;
  const hasMore = pageCount < totalPages;
  const showMore = useCallback(() => {
    setPageCount((count) => (count < totalPages ? count + 1 : count));
  }, [totalPages]);

  return useMemo(
    () => ({
      firstPage: first.data,
      firstPageLoading: first.isLoading,
      firstPageError: first.error,
      retryFirstPage,
      pageCount,
      totalOfAllTabs: everyStatus.data?.totalItems,
      hasMore,
      loadingMore,
      moreError: pageCount > 1 ? last.error : undefined,
      retryMore,
      showMore,
    }),
    [
      first.data,
      first.isLoading,
      first.error,
      retryFirstPage,
      pageCount,
      everyStatus.data?.totalItems,
      hasMore,
      loadingMore,
      last.error,
      retryMore,
      showMore,
    ]
  );
}
